import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  CircularProgress,
} from "@mui/material";
import { IoMailOutline, IoCheckmark, IoArrowBack } from "react-icons/io5";
import config from "../config";
import { adminColors, TOUCH_HIT_SX } from "../theme/adminTheme";
import { FONT_STACK, BORDER_COLOR, BrandHeader } from "./legal/LegalPageLayout";

// Shared focus treatment for form fields — indigo ring/label, no default MUI
// asterisk. Kept identical to Signin.js / RequestAccess.js's fieldFocusSx so
// every logged-out auth surface reads as one flow.
const fieldFocusSx = {
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: adminColors.accent,
    borderWidth: "1.5px",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: adminColors.accent,
  },
  "& .MuiFormLabel-asterisk": {
    display: "none",
  },
};

/**
 * /forgot-password — a single "username or email" field. The backend always
 * answers 200 on this endpoint, identically, whether or not the account (or
 * an email on it) exists — deliberate anti-enumeration behavior — so the UI
 * never branches on "did we find you," only on "did the request itself
 * fail." The confirmation copy has to work for accounts with no email on
 * file too (common here: many student accounts are username-only), so it
 * always names the fallback path (ask a teacher/admin) rather than implying
 * an email is always on the way.
 */
export default function ForgotPassword() {
  const navigate = useNavigate();

  const [identifier, setIdentifier] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [mounted, setMounted] = useState(false);

  const identifierRef = useRef(null);
  const confirmationHeadingRef = useRef(null);

  useEffect(() => {
    identifierRef.current?.focus();
    setMounted(true);
  }, []);

  // Move focus to the confirmation so screen reader users get the outcome
  // announced instead of landing on a page that silently changed under them.
  useEffect(() => {
    if (submitted) {
      confirmationHeadingRef.current?.focus();
    }
  }, [submitted]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) return;
    setError("");
    setSubmitting(true);

    try {
      const res = await fetch(`${config.API_BASE_URL}/api/password-reset/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: identifier.trim() }),
      });

      // Contract: this endpoint always answers 200 regardless of whether the
      // account/email exists (no user enumeration). The only thing worth
      // branching on client-side is a genuine request failure, never
      // "found"/"not found" — that distinction never reaches the browser.
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError("Something went wrong on our end. Please try again.");
      }
    } catch (err) {
      console.error("Password reset request failed:", err?.message ?? err);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!mounted) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: adminColors.surface,
        }}
      >
        <CircularProgress sx={{ color: adminColors.accent }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: adminColors.surface,
        display: "flex",
        flexDirection: "column",
        fontFamily: FONT_STACK,
        "@media (prefers-reduced-motion: reduce)": {
          "*": {
            transitionDuration: "0.01ms !important",
            animationDuration: "0.01ms !important",
          },
        },
      }}
    >
      <BrandHeader />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Card
          sx={{
            width: "100%",
            maxWidth: 420,
            p: 4,
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            border: `1px solid ${BORDER_COLOR}`,
            borderRadius: "12px",
          }}
        >
          {submitted ? (
            // Same shape whether or not an account/email actually exists —
            // see the contract note in handleSubmit above.
            <Box
              sx={{
                textAlign: "center",
                animation: "forgotPwConfirmIn 320ms cubic-bezier(0.23, 1, 0.32, 1)",
                "@keyframes forgotPwConfirmIn": {
                  from: { opacity: 0, transform: "translateY(8px) scale(0.98)" },
                  to: { opacity: 1, transform: "translateY(0) scale(1)" },
                },
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  backgroundColor: adminColors.accent,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 3,
                }}
              >
                <IoCheckmark size={24} color="white" />
              </Box>

              <Typography
                ref={confirmationHeadingRef}
                tabIndex={-1}
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: adminColors.ink,
                  mb: 1.5,
                  fontSize: { xs: "1.35rem", sm: "1.6rem" },
                  lineHeight: 1.3,
                  outline: "none",
                }}
              >
                Check your email
              </Typography>

              <Typography variant="body1" sx={{ color: adminColors.inkSoft, lineHeight: 1.65, mb: 1.5 }}>
                If that account has an email on file, we've sent a reset link.
              </Typography>
              {/* Same variant/weight as the sentence above, deliberately: this
                  applies to a large share of accounts (many are username-only,
                  with no email on file at all), so it isn't a lesser footnote. */}
              <Typography variant="body1" sx={{ color: adminColors.inkSoft, lineHeight: 1.65, mb: 4 }}>
                No email on file? Ask your teacher or school admin to reset your password.
              </Typography>

              <Button
                variant="outlined"
                onClick={() => navigate("/signin")}
                startIcon={<IoArrowBack size={16} />}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "8px",
                  borderColor: BORDER_COLOR,
                  color: adminColors.ink,
                  "&:hover": {
                    borderColor: adminColors.accent,
                    backgroundColor: adminColors.accentLight,
                  },
                  ...TOUCH_HIT_SX,
                }}
              >
                Back to sign in
              </Button>
            </Box>
          ) : (
            <>
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    backgroundColor: adminColors.accent,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 3,
                  }}
                >
                  <IoMailOutline size={22} color="white" />
                </Box>

                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: adminColors.ink,
                    mb: 1,
                    fontSize: { xs: "1.5rem", sm: "1.85rem" },
                    lineHeight: 1.25,
                  }}
                >
                  Forgot your password?
                </Typography>

                <Typography variant="body2" sx={{ color: adminColors.inkSoft, lineHeight: 1.6 }}>
                  Enter your username or email and we'll send a reset link if we find a match.
                </Typography>
              </Box>

              {error && (
                <Alert
                  severity="error"
                  sx={{ mb: 3, "& .MuiAlert-message": { fontSize: "14px" } }}
                >
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <TextField
                  fullWidth
                  label="Username or email"
                  autoComplete="username"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  ref={identifierRef}
                  required
                  sx={{ mb: 3, ...fieldFocusSx }}
                  disabled={submitting}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={submitting || !identifier.trim()}
                  sx={{
                    mb: 3.5,
                    py: 1.5,
                    backgroundColor: adminColors.accent,
                    "&:hover": {
                      backgroundColor: adminColors.accentDark,
                    },
                    "&.Mui-disabled": {
                      backgroundColor: adminColors.accentBorder,
                      color: adminColors.accentDark,
                    },
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 15,
                    borderRadius: "8px",
                    transition: "transform 120ms ease-out, background-color 150ms ease",
                    "&:active": {
                      transform: submitting ? "none" : "scale(0.98)",
                    },
                  }}
                >
                  {submitting ? (
                    <CircularProgress size={20} sx={{ color: adminColors.accentDark }} />
                  ) : (
                    "Send reset link"
                  )}
                </Button>
              </Box>

              <Box sx={{ textAlign: "center" }}>
                <Typography variant="body2" sx={{ color: adminColors.inkSoft }}>
                  Remembered it?{" "}
                  <Link
                    component="button"
                    type="button"
                    onClick={() => navigate("/signin")}
                    sx={{
                      fontWeight: 600,
                      color: adminColors.accent,
                      cursor: "pointer",
                      verticalAlign: "baseline",
                      "&:hover": { color: adminColors.accentDark },
                      ...TOUCH_HIT_SX,
                    }}
                  >
                    Back to sign in
                  </Link>
                </Typography>
              </Box>
            </>
          )}
        </Card>
      </Box>
    </Box>
  );
}
